import React from "react"
import { UseStylePropsProps, StyleProps } from "./props"
import { NamedStyle, GenericStyleProp } from "./styles"

export interface DomProps {
  accessibilityRole?: string
  accessibilityLabel?: string
  accessibilityLiveRegion?: "none" | "polite" | "assertive"
  id?: string
  nativeID?: string
  pointerEvents?: "box-none" | "none" | "box-only" | "auto"
  style?: GenericStyleProp<NamedStyle>
  testID?: string
  needsReset?: boolean
  needsCursor?: boolean
  classList?: string[]
}

export interface StyledDomProps extends Omit<StyleProps, "style">, DomProps {}

export type As = React.ElementType

export type PropsOf<T extends As> = React.ComponentPropsWithoutRef<T> & {
  as?: As
}

export type OmitCommonProps<
  Target,
  OmitAdditionalProps extends keyof any = never
> = Omit<Target, "as" | OmitAdditionalProps>

export type RightJoinProps<
  SourceProps extends object = {},
  OverrideProps extends object = {}
> = OmitCommonProps<SourceProps, keyof OverrideProps> & OverrideProps

export type MergeWithAs<
  ComponentProps extends object,
  AsProps extends object,
  AdditionalProps extends object = {},
  AsComponent extends As = As
> = RightJoinProps<ComponentProps, AdditionalProps> &
  RightJoinProps<AsProps, AdditionalProps> & {
    as?: AsComponent
  }

export type ComponentWithAs<Component extends As, Props extends object = {}> = {
  <AsComponent extends As>(
    props: MergeWithAs<
      React.ComponentProps<Component>,
      React.ComponentProps<AsComponent>,
      Props,
      AsComponent
    >,
  ): JSX.Element

  displayName?: string
  propTypes?: React.WeakValidationMap<any>
  contextTypes?: React.ValidationMap<any>
  defaultProps?: Partial<any>
  id?: string
}

export interface StyledComponent<T extends As, P = {}>
  extends ComponentWithAs<T, P & StyledDomProps> {}

export type HTMLStyledProps<T extends As> = Omit<
  PropsOf<T>,
  T extends "svg"
    ? "ref" | "children" | keyof StyleProps
    : "ref" | keyof StyleProps
> &
  StyledDomProps & { as?: As }

export type StyledElementType = UseStylePropsProps<DomProps>

export const domElements = [
  "a",
  "b",
  "article",
  "aside",
  "blockquote",
  "button",
  "caption",
  "cite",
  "circle",
  "code",
  "dd",
  "div",
  "dl",
  "dt",
  "fieldset",
  "figcaption",
  "figure",
  "footer",
  "form",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "header",
  "hr",
  "img",
  "input",
  "kbd",
  "label",
  "li",
  "mark",
  "nav",
  "ol",
  "p",
  "path",
  "pre",
  "q",
  "rect",
  "s",
  "svg",
  "section",
  "select",
  "strong",
  "small",
  "span",
  "sub",
  "sup",
  "table",
  "tbody",
  "td",
  "textarea",
  "tfoot",
  "th",
  "thead",
  "tr",
  "ul",
] as const

export type DOMElements = typeof domElements[number]

export type StyledHTMLComponents = {
  [Tag in DOMElements]: StyledComponent<Tag, HTMLStyledProps<Tag>>
}
